import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Decimal, dateDuJour } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { BandeauAlerte } from './BandeauAlerte';
import { Champ } from './Champ';
import { MenuActions, type ElementMenu } from './MenuActions';
import { Modale } from './Modale';

/** Ce que les gestes sur un mandat ont besoin d'en connaître. */
export interface MandatActionnable {
  id: string;
  reference: string;
  statut: string;
  creePar: string;
  profil: { id: string };
  profilDateDetermination: string | null;
  profilReferenceDossier: string | null;
  dateSignature: string | null;
  ibanCompteEspece: string | null;
  tauxFraisAnnuelPct: string;
  tamponTresoreriePct: string;
}

const VIDE_PROFIL = () => ({
  profilId: '',
  dateDetermination: dateDuJour(),
  dateEffet: dateDuJour(),
  referenceDossier: '',
  motif: '',
});

const NOTE = { fontSize: 13, color: 'var(--texte-doux)', marginBottom: 'var(--e4)' } as const;

/**
 * Gestes sur un mandat, portés par le menu « … » de la liste des mandats :
 * cycle de vie (soumission, validation, suspension), édition, changement de
 * profil, rebalancing, et raccourci vers la sortie de fonds.
 *
 * Un seul jeu de modales pour toute la liste : le menu d'une ligne ne fait
 * que désigner le mandat visé. Les contrôles (rôles, quatre yeux, conditions
 * d'activation, unicité de la demande de sortie) restent au serveur ;
 * l'interface ne fait que ne pas proposer ce qui serait refusé à coup sûr.
 */
export function useActionsMandat({ surChangement }: { surChangement: () => unknown }): {
  menu: (m: MandatActionnable) => ReactNode;
  modales: ReactNode;
  erreur: ReactNode;
} {
  const naviguer = useNavigate();
  const { profil: utilisateur, aRole } = useAuth();
  const peutSaisir = aRole('MIDDLE_BO', 'RESP_GESTION', 'ADMIN');
  const peutValider = aRole('RESP_GESTION');
  const peutGerer = aRole('GERANT', 'RESP_GESTION', 'ADMIN');

  const [occupe, setOccupe] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  // Une modale ouverte à la fois, sur un mandat désigné.
  const [cible, setCible] = useState<MandatActionnable | null>(null);
  const [modale, setModale] = useState<'edition' | 'profil' | 'suspension' | null>(null);
  const [erreurModale, setErreurModale] = useState<string | null>(null);

  const [saisie, setSaisie] = useState({
    profilDateDetermination: '',
    profilReferenceDossier: '',
    dateSignature: '',
    ibanCompteEspece: '',
    tauxFraisAnnuelPct: '',
    tamponTresoreriePct: '',
  });
  const [saisieProfil, setSaisieProfil] = useState(VIDE_PROFIL);
  const [motifSuspension, setMotifSuspension] = useState('');

  // Le référentiel des profils n'est chargé qu'à l'ouverture du changement.
  const profils = useRessource<{ id: string; libelle: string }[]>(
    modale === 'profil' ? '/profils' : null,
  );

  const champ = (cle: keyof typeof saisie) => ({
    value: saisie[cle],
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  function ouvrir(m: MandatActionnable, nom: NonNullable<typeof modale>) {
    setCible(m);
    setErreurModale(null);
    setErreurAction(null);
    if (nom === 'edition') {
      setSaisie({
        profilDateDetermination: m.profilDateDetermination
          ? m.profilDateDetermination.slice(0, 10)
          : dateDuJour(),
        profilReferenceDossier: m.profilReferenceDossier ?? '',
        dateSignature: m.dateSignature ? m.dateSignature.slice(0, 10) : dateDuJour(),
        ibanCompteEspece: m.ibanCompteEspece ?? '',
        tauxFraisAnnuelPct: new Decimal(m.tauxFraisAnnuelPct).toString(),
        tamponTresoreriePct: new Decimal(m.tamponTresoreriePct).toString(),
      });
    }
    if (nom === 'profil') setSaisieProfil(VIDE_PROFIL());
    if (nom === 'suspension') setMotifSuspension('');
    setModale(nom);
  }

  function fermer() {
    setModale(null);
  }

  async function agir(appel: () => Promise<unknown>) {
    setErreurAction(null);
    setOccupe(true);
    try {
      await appel();
      await surChangement();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function rebalancer(m: MandatActionnable) {
    setErreurAction(null);
    setOccupe(true);
    try {
      const c = await api.post<{ id: string }>('/campagnes/rebalancing', { mandatIds: [m.id] });
      naviguer(`/campagnes/${c.id}`);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Rebalancing impossible.');
    } finally {
      setOccupe(false);
    }
  }

  /** Soumission d'une modale : ferme et rafraîchit, ou garde l'erreur en tête. */
  async function soumettreModale(
    evenement: { preventDefault: () => void },
    appel: () => Promise<unknown>,
    repli: string,
  ) {
    evenement.preventDefault();
    setErreurModale(null);
    setOccupe(true);
    try {
      await appel();
      setModale(null);
      await surChangement();
    } catch (e) {
      setErreurModale(e instanceof ErreurApi ? messageErreur(e) : repli);
    } finally {
      setOccupe(false);
    }
  }

  function menu(m: MandatActionnable): ReactNode {
    const actif = m.statut === 'ACTIF';
    // L'ADMIN valide ses propres opérations (décision du 04/08/2026) ; pour
    // tout autre rôle, l'auteur du dossier ne peut pas le valider.
    const auteur = utilisateur?.id === m.creePar && !aRole('ADMIN');

    const consulter: ElementMenu[] = [
      {
        libelle: 'Voir le portefeuille',
        icone: 'portefeuilles',
        vers: `/mandats/${m.id}/portefeuille`,
      },
      { libelle: 'Voir la trésorerie', icone: 'tresorerie', vers: `/mandats/${m.id}/tresorerie` },
    ];

    const gerer: ElementMenu[] = [];
    if (peutGerer && actif)
      gerer.push({
        libelle: 'Rebalancer ce mandat',
        icone: 'campagnes',
        desactive: occupe,
        surClic: () => void rebalancer(m),
      });
    if (peutGerer && (actif || m.statut === 'EN_LIQUIDATION'))
      // La saisie vit dans Gestion › Sorties de fonds ; le menu y mène, mandat choisi.
      gerer.push({
        libelle: 'Libérer des fonds',
        icone: 'sorties',
        vers: `/sorties-fonds?mandatId=${m.id}`,
      });
    if (peutValider && (actif || m.statut === 'SUSPENDU'))
      gerer.push({
        libelle: 'Changer le profil',
        icone: 'profils',
        surClic: () => ouvrir(m, 'profil'),
      });

    const dossier: ElementMenu[] = [];
    if (peutSaisir && m.statut === 'BROUILLON') {
      dossier.push({
        libelle: 'Éditer le mandat',
        icone: 'editer',
        surClic: () => ouvrir(m, 'edition'),
      });
      dossier.push({
        libelle: 'Soumettre à validation',
        icone: 'mandats',
        desactive: occupe,
        surClic: () => void agir(() => api.post(`/mandats/${m.id}/soumettre`)),
      });
    }
    if (peutValider && m.statut === 'EN_ATTENTE_VALIDATION')
      dossier.push({
        libelle: 'Valider et activer',
        icone: 'controle',
        desactive: occupe || auteur,
        aide: auteur
          ? 'Vous avez saisi ce mandat : sa validation revient à une autre personne (quatre yeux).'
          : undefined,
        surClic: () => void agir(() => api.post(`/mandats/${m.id}/valider`)),
      });
    if (peutValider && actif)
      dossier.push({
        libelle: 'Suspendre',
        icone: 'gestion',
        surClic: () => ouvrir(m, 'suspension'),
      });
    if (peutValider && m.statut === 'SUSPENDU')
      dossier.push({
        libelle: 'Réactiver',
        icone: 'investir',
        desactive: occupe,
        surClic: () => void agir(() => api.post(`/mandats/${m.id}/reactiver`)),
      });

    return (
      <MenuActions libelle={`Actions sur ${m.reference}`} groupes={[consulter, gerer, dossier]} />
    );
  }

  const erreur = erreurAction && (
    <BandeauAlerte nature="refus" titre="Opération refusée">
      {erreurAction}
    </BandeauAlerte>
  );

  const bandeauModale = (titre: string) =>
    erreurModale && (
      <BandeauAlerte nature="refus" titre={titre}>
        {erreurModale}
      </BandeauAlerte>
    );

  const annuler = (
    <button type="button" className="bouton bouton--fantome" onClick={fermer}>
      Annuler
    </button>
  );

  const m = cible;
  const modales = m && (
    <>
      <Modale
        ouverte={modale === 'edition'}
        onFermer={fermer}
        surtitre={`Mandat · ${m.reference}`}
        titre="Éditer le mandat"
        taille="large"
      >
        {bandeauModale('Enregistrement refusé')}
        <p style={NOTE}>
          Le profil de risque et l’agence se modifient par leurs flux dédiés. Ici, les éléments
          reçus de l’agence et les conditions du mandat.
        </p>
        <form
          onSubmit={(e) =>
            void soumettreModale(
              e,
              () =>
                api.patch(`/mandats/${m.id}`, {
                  profilDateDetermination: saisie.profilDateDetermination || undefined,
                  profilReferenceDossier: saisie.profilReferenceDossier || undefined,
                  dateSignature: saisie.dateSignature || undefined,
                  ibanCompteEspece: saisie.ibanCompteEspece || undefined,
                  tauxFraisAnnuelPct: saisie.tauxFraisAnnuelPct || undefined,
                  tamponTresoreriePct: saisie.tamponTresoreriePct || undefined,
                }),
              'Enregistrement impossible.',
            )
          }
          noValidate
        >
          <div className="champs-en-ligne">
            <Champ
              label="Date de détermination du profil"
              type="date"
              {...champ('profilDateDetermination')}
            />
            <Champ label="Référence du dossier de profilage" {...champ('profilReferenceDossier')} />
          </div>
          <div className="champs-en-ligne">
            <Champ label="Date de signature du mandat" type="date" {...champ('dateSignature')} />
            <Champ label="RIB / IBAN du compte espèces" {...champ('ibanCompteEspece')} />
          </div>
          <div className="champs-en-ligne">
            <Champ label="Frais annuels (%)" montant {...champ('tauxFraisAnnuelPct')} />
            <Champ label="Tampon trésorerie (%)" montant {...champ('tamponTresoreriePct')} />
          </div>
          <div className="modale__actions">
            {annuler}
            <button className="bouton" type="submit" disabled={occupe}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>

      <Modale
        ouverte={modale === 'profil'}
        onFermer={fermer}
        surtitre={`Mandat · ${m.reference}`}
        titre="Changer le profil"
        taille="large"
      >
        {bandeauModale('Changement refusé')}
        <p style={NOTE}>
          Profil déterminé en agence, reçu ici. Le taux de frais et le tampon de trésorerie ne
          changent pas : ils sont portés par le mandat. Aucun ordre n’est généré : le mandat entre
          dans la liste « à réaligner », et le rebalancing reste à lancer.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErreurModale(null);
            setOccupe(true);
            api
              .post(`/mandats/${m.id}/changer-profil`, {
                profilId: saisieProfil.profilId,
                dateDetermination: saisieProfil.dateDetermination,
                dateEffet: saisieProfil.dateEffet,
                referenceDossier: saisieProfil.referenceDossier || undefined,
                motif: saisieProfil.motif,
              })
              .then(async () => {
                setModale(null);
                await surChangement();
              })
              .catch((err: unknown) =>
                setErreurModale(
                  err instanceof ErreurApi ? messageErreur(err) : 'Changement impossible.',
                ),
              )
              .finally(() => setOccupe(false));
          }}
          noValidate
        >
          <div className="champ">
            <label className="surtitre" htmlFor="nouveau-profil">
              Nouveau profil
            </label>
            <select
              id="nouveau-profil"
              className="champ__select"
              value={saisieProfil.profilId}
              onChange={(e) => setSaisieProfil({ ...saisieProfil, profilId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(profils.donnees ?? [])
                // Le profil courant est retiré : le serveur le refuserait.
                .filter((p) => p.id !== m.profil.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.libelle}
                  </option>
                ))}
            </select>
          </div>
          <div className="champs-en-ligne">
            <Champ
              label="Date de détermination"
              type="date"
              value={saisieProfil.dateDetermination}
              onChange={(e) =>
                setSaisieProfil({ ...saisieProfil, dateDetermination: e.target.value })
              }
              aide="Date à laquelle l’agence a arrêté le nouveau profil."
              required
            />
            <Champ
              label="Date d’effet"
              type="date"
              value={saisieProfil.dateEffet}
              onChange={(e) => setSaisieProfil({ ...saisieProfil, dateEffet: e.target.value })}
              aide="Bascule de l’historique de profil."
              required
            />
          </div>
          <Champ
            label="Référence du dossier de profilage"
            value={saisieProfil.referenceDossier}
            onChange={(e) => setSaisieProfil({ ...saisieProfil, referenceDossier: e.target.value })}
          />
          <Champ
            label="Motif du changement"
            value={saisieProfil.motif}
            onChange={(e) => setSaisieProfil({ ...saisieProfil, motif: e.target.value })}
            aide="Obligatoire — conservé dans l’historique de profil."
            required
          />
          <div className="modale__actions">
            {annuler}
            <button
              className="bouton"
              type="submit"
              disabled={
                occupe ||
                !saisieProfil.profilId ||
                !saisieProfil.dateDetermination ||
                !saisieProfil.dateEffet ||
                !saisieProfil.motif.trim()
              }
            >
              {occupe ? 'Enregistrement…' : 'Changer le profil'}
            </button>
          </div>
        </form>
      </Modale>

      <Modale
        ouverte={modale === 'suspension'}
        onFermer={fermer}
        surtitre={`Mandat · ${m.reference}`}
        titre="Suspendre le mandat"
      >
        {bandeauModale('Suspension refusée')}
        <form
          onSubmit={(e) =>
            void soumettreModale(
              e,
              () => api.post(`/mandats/${m.id}/suspendre`, { motif: motifSuspension.trim() }),
              'Suspension impossible.',
            )
          }
          noValidate
        >
          <Champ
            label="Motif de la suspension"
            value={motifSuspension}
            onChange={(e) => setMotifSuspension(e.target.value)}
            aide="Obligatoire — affiché sur la fiche tant que le mandat reste suspendu."
            required
          />
          <div className="modale__actions">
            {annuler}
            <button className="bouton" type="submit" disabled={occupe || !motifSuspension.trim()}>
              {occupe ? 'Suspension…' : 'Suspendre'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );

  return { menu, modales, erreur };
}
